import _ from 'lodash';
import {all, put, takeLatest} from 'redux-saga/effects';
import request, {GET, POST} from '../../services/request';
import * as actionTypes from '../actions/actionTypes';
import {handleError, sleep, ValidationException} from './common';
import {API_URL} from '../../configuration/config';
import {VerificationCodeType} from '../../services/constants';
import {remove, set} from '../../services/store';
import {usernameIsValid, passwordIsValid, emailIsValid, walletIsValid} from '../../services/validation';
import i18n from '../../configuration/i18n';

function *login({payload}) {
  try {
    const response = yield request(POST, API_URL + 'login/', payload);
    yield put({type: actionTypes.LOG_IN_SUCCESS, payload: response.data});
    set('token', response.data.token);
    if (payload.callback) {
      payload.callback(response.data);
    }
  } catch (error) {
    yield handleError({error, ignore: [400]});
    yield put({type: actionTypes.LOG_IN_FAILED, error});
  }
}

function logout() {
  remove('token');
}

function *signup({payload}) {
  try {
    if (payload.password !== payload.passwordRepeat) {
      yield sleep(100);
      throw new ValidationException({
        passwordRepeat: [i18n.t('errors.validation.valueMatch')],
      });
    }
    if (!usernameIsValid(payload.username)) {
      yield sleep(100);
      throw new ValidationException({
        username: [i18n.t('errors.validation.username')],
      });
    }
    if (!passwordIsValid(payload.password)) {
      yield sleep(100);
      throw new ValidationException({
        password: [i18n.t('errors.validation.password')],
      });
    }
    if (payload.wallet && !walletIsValid(payload.wallet)) {
      yield sleep(100);
      throw new ValidationException({
        wallet: [i18n.t('errors.validation.wallet')],
      });
    }
    const response = yield request(
      POST,
      API_URL + 'users/',
      _.pick(payload, ['username', 'password', 'wallet']),
      {headers: {'X-Verification-Code': payload.activationCode}}
      );
    yield put({type: actionTypes.SIGN_UP_SUCCESS, payload: response.data});
    if (payload.callback) {
      payload.callback(response.data);
    }
  } catch (error) {
    yield handleError({error, ignore: [400]});
    yield put({type: actionTypes.SIGN_UP_FAILED, error});
  }
}

function *requestActivationCode(action) {
  try {
    if (!action.payload.agreed) {
      yield sleep(100);
      throw new ValidationException({
        agreed: [i18n.t('errors.validation.requiredField')],
      });
    }
    yield request(
      POST,
      API_URL + 'verification_codes/',
      {
        email: action.payload.email,
        code_type: VerificationCodeType.ACTIVATE_ACCOUNT,
      }
    );
    yield put({type: actionTypes.REQUEST_ACTIVATION_CODE_SUCCESS});
    if (action.payload.callback) {
      action.payload.callback();
    }
  } catch (error) {
    yield put({type: actionTypes.REQUEST_ACTIVATION_CODE_FAILED, error});
    yield handleError({error, ignore: [400]});
  }
}

function *requestPasswordResetCode(action) {
  try {
    if (!emailIsValid(action.payload.email)) {
      yield sleep(100);
      throw new ValidationException({
        email: [i18n.t('errors.validation.email')],
      });
    }
    yield request(
      POST,
      API_URL + 'verification_codes/',
      {
        email: action.payload.email,
        code_type: VerificationCodeType.RESET_PASSWORD,
      }
    );
    yield put({type: actionTypes.REQUEST_RESET_CODE_SUCCESS});
    if (action.payload.callback) {
      action.payload.callback();
    }
  } catch (error) {
    yield handleError({error, ignore: [400]});
    yield put({type: actionTypes.REQUEST_RESET_CODE_FAILED, error});
    if (action.payload.onError) {
      action.payload.onError();
    }
  }
}

function *requestProfileEditCode(action) {
  try {
    yield request(
      POST,
      API_URL + 'verification_codes/',
      {
        email: action.payload.email,
        code_type: VerificationCodeType.UPDATE_FRAGILE_DATA,
      }
    );
    yield put({type: actionTypes.REQUEST_PROFILE_EDIT_CODE_SUCCESS});
    if (action.payload.callback) {
      action.payload.callback();
    }
  } catch (error) {
    yield handleError({error});
    yield put({type: actionTypes.REQUEST_PROFILE_EDIT_CODE_FAILED, error});
  }
}

function *requestEmailEditCode(action) {
  try {
    let validationError;
    if (action.payload.email !== action.payload.emailRepeat) {
      validationError = 'errors.validation.valueMatch';
    } else if (!emailIsValid(action.payload.email)) {
      validationError = 'errors.validation.email';
    }
    if (validationError) {
      yield sleep(100);
      throw new ValidationException({
        emailRepeat: [i18n.t(validationError)],
      });
    }
    yield request(
      POST,
      API_URL + 'verification_codes/',
      {
        email: action.payload.email,
        code_type: VerificationCodeType.VERIFY_NEW_EMAIL,
      }
    );
    yield put({type: actionTypes.REQUEST_EMAIL_EDIT_CODE_SUCCESS});
    if (action.payload.callback) {
      action.payload.callback();
    }
  } catch (error) {
    yield handleError({error, ignore: [400]});
    yield put({type: actionTypes.REQUEST_EMAIL_EDIT_CODE_FAILED, error});
  }
}

function *resetPassword(action) {
  try {
    yield request(POST, API_URL + 'users/reset_password/', {}, {headers: {'X-Verification-Code': action.payload.reset_code}});
    yield put({type: actionTypes.RESET_PASSWORD_SUCCESS});
    if (action.payload.callback) {
      action.payload.callback();
    }
  } catch (error) {
    yield handleError({error});
    yield put({type: actionTypes.RESET_PASSWORD_FAILED, error});
  }
}

function *validateVerificationCode(action) {
  try {
    yield request(GET, API_URL + `verification_codes/${action.payload.code}/?expected_code_type=${action.payload.type}`);
    yield put({type: actionTypes.VALIDATE_CODE_SUCCESS});
    if (action.payload.callback) {
      action.payload.callback();
    }
  } catch (error) {
    yield handleError({error, ignore: [404]});
    yield put({type: actionTypes.VALIDATE_CODE_FAILED, error});
    if (action.payload.onError) {
      action.payload.onError();
    }
  }
}

export default function *auth() {
  yield all([
    yield takeLatest(actionTypes.LOG_IN, login),
    yield takeLatest(actionTypes.LOG_OUT, logout),
    yield takeLatest(actionTypes.SIGN_UP, signup),
    yield takeLatest(actionTypes.REQUEST_ACTIVATION_CODE, requestActivationCode),
    yield takeLatest(actionTypes.REQUEST_RESET_CODE, requestPasswordResetCode),
    yield takeLatest(actionTypes.REQUEST_PROFILE_EDIT_CODE, requestProfileEditCode),
    yield takeLatest(actionTypes.REQUEST_EMAIL_EDIT_CODE, requestEmailEditCode),
    yield takeLatest(actionTypes.RESET_PASSWORD, resetPassword),
    yield takeLatest(actionTypes.VALIDATE_CODE, validateVerificationCode),
  ]);
}



// WEBPACK FOOTER //
// /src/redux/saga/auth.js